// ─────────────────────────────────────────────
// Oike · Mi marca › «Cuaderno de trabajo» — the PDF, drawn from the same layout as the preview (src/notebook.js).
// Text is real text in the embedded font files (subsets), so it can be selected, copied and searched; strokes are
// vector paths; photos keep their transparency. pdf-lib and fontkit are passed in, so the same code runs in the
// browser (loaded only when downloading) and in the tests.
// ─────────────────────────────────────────────

const FULL_FONTS = new Set(['title', 'note']);
// Caveat's contextual alternates (handwriting variants) do not survive in the PDF: they are turned off for that font,
// in the measure and in the embedding alike, so widths and drawing agree.
export const FONT_FEATURES = Object.freeze({ note: { calt: false, liga: false, clig: false, dlig: false } });
const hex = (rgb, h) => { const n = parseInt(String(h).slice(1), 16); return rgb(((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255); };

/** A measure function over fontkit fonts: width of a text in points, as pdf-lib draws it (sum of advances). */
export function makeMeasure(fontkitFonts) {
  const cache = new Map();
  const measure = (text, key, size) => {
    const font = fontkitFonts[key];
    if (!font) throw new Error(`font ${key} not loaded`);
    const k = `${key}\u0000${text}`;
    let units = cache.get(k);
    if (units === undefined) {
      units = 0;
      for (const g of font.layout(String(text), FONT_FEATURES[key]).glyphs) units += g.advanceWidth;
      units /= font.unitsPerEm;
      cache.set(k, units);
    }
    return units * size;
  };
  /** Whether a font has every character of a text (a missing one would be drawn as an empty box in the files). */
  measure.covers = (text, key) => { const font = fontkitFonts[key]; return !font || font.glyphsForString(String(text).replace(/\s/g, '')).every(g => g.id !== 0); };
  return measure;
}

/**
 * The PDF bytes. fonts: { key: Uint8Array } (the identity's font files), texture: Uint8Array (PNG) or null,
 * images: { photoId: { bytes: Uint8Array, type: 'png' | 'jpg' } }.
 */
export async function buildNotebookPdf(layout, { PDFLib, fontkit, fonts, texture = null, images = {}, title = '', author = '', subject = '' }) {
  const { PDFDocument, rgb, pushGraphicsState, popGraphicsState, rectangle, clip, endPath, LineCapStyle } = PDFLib;
  const doc = await PDFDocument.create();
  doc.registerFontkit(fontkit);
  doc.setTitle(title || 'Cuaderno de trabajo', { showInWindowTitleBar: true });
  if (author) doc.setAuthor(author);
  if (subject) doc.setSubject(subject);
  doc.setLanguage('es');
  doc.setCreator('Oike · Mi marca');
  doc.setProducer('Oike');
  const used = new Set(layout.pages.flatMap(p => p.items.filter(i => i.t === 'text').map(i => i.font)));
  const embedded = {};
  // Handwritten fonts are embedded whole: pdf-lib's subsetting drops glyphs of these files (checked in the tests by
  // rendering); the text fonts are subset to keep the file small. Either way the text stays real, searchable text.
  for (const key of used) embedded[key] = await doc.embedFont(fonts[key], { subset: !FULL_FONTS.has(key), ...(FONT_FEATURES[key] ? { features: FONT_FEATURES[key] } : {}) });
  const tex = texture && layout.pages.some(p => p.items.some(i => i.t === 'texture')) ? await doc.embedPng(texture) : null;
  const imgs = {};
  for (const [id, im] of Object.entries(images)) imgs[id] = im.type === 'png' ? await doc.embedPng(im.bytes) : await doc.embedJpg(im.bytes);
  const { w: W, h: H } = layout.size;
  for (const page of layout.pages) {
    const p = doc.addPage([W, H]);
    for (const it of page.items) {
      if (it.t === 'rect') p.drawRectangle({ x: it.x, y: H - it.y - it.h, width: it.w, height: it.h, color: hex(rgb, it.fill) });
      else if (it.t === 'texture') { if (tex) p.drawImage(tex, { x: 0, y: 0, width: W, height: H }); }
      else if (it.t === 'path') {
        p.drawSvgPath(it.d, {
          x: 0, y: H,
          ...(it.stroke ? { borderColor: hex(rgb, it.stroke), borderWidth: it.width, borderOpacity: it.opacity ?? 1, borderLineCap: LineCapStyle.Round } : { borderWidth: 0 }),
          ...(it.fill ? { color: hex(rgb, it.fill), opacity: it.opacity ?? 1 } : {}),
        });
      } else if (it.t === 'text') p.drawText(it.text, { x: it.x, y: H - it.y, size: it.size, font: embedded[it.font], color: hex(rgb, it.color) });
      else if (it.t === 'image') {
        const img = imgs[it.photoId];
        if (!img) continue;
        const c = it.clip;
        p.pushOperators(pushGraphicsState(), rectangle(c.x, H - c.y - c.h, c.w, c.h), clip(), endPath());
        p.drawImage(img, { x: it.x, y: H - it.y - it.h, width: it.w, height: it.h });
        p.pushOperators(popGraphicsState());
      }
    }
  }
  return doc.save();
}
